import { useEffect, useRef } from "react";
import { api } from "./api";
import { useToast } from "./ToastContext";

// Previously the bell badge only refreshed on navigation (view change) or
// pull-to-refresh — an owner sitting on one screen for a while never found
// out about a new visit request or comment until they happened to switch
// tabs. This polls in the background and toasts anything genuinely new, so
// "notifications should come" holds while the app is actively open, not
// just via background push.
const POLL_MS = 30000;
const listeners = new Set<() => void>();

// Lets a native foreground push (see App.tsx's pushNotificationReceived
// listener) nudge an already-mounted poll to run immediately instead of
// waiting up to POLL_MS for the toast/badge to catch up.
export function triggerNotificationPoll() {
  listeners.forEach((l) => l());
}

export function useLiveNotifications(setUnreadCount: (n: number) => void) {
  const toast = useToast();
  const lastSeenIdRef = useRef<string | null>(null);
  const primedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const { notifications, unreadCount } = await api.get("/api/notifications/mine");
        if (cancelled) return;
        setUnreadCount(unreadCount);
        const latest = notifications[0];
        if (!latest) return;
        if (!primedRef.current) {
          // The first call just establishes the baseline — nothing that
          // already existed before this screen mounted should toast.
          primedRef.current = true;
        } else if (latest.id !== lastSeenIdRef.current) {
          toast(latest.title);
        }
        lastSeenIdRef.current = latest.id;
      } catch {
        // a failed poll just tries again next tick
      }
    }
    poll();
    const id = setInterval(poll, POLL_MS);
    listeners.add(poll);
    return () => {
      cancelled = true;
      clearInterval(id);
      listeners.delete(poll);
    };
  }, [setUnreadCount, toast]);
}
